import { useState, useRef, useEffect } from "react";
import { useUserContext } from "../../../../contexts/UserContext";
import {
  getRowObjectToBeDeleted,
  getSectionTooltipInfo,
  getTitleFromKey,
} from "../../helpers";
import {
  RowDataObject,
  JsonSchemaItems,
  RenderCoRelationProps,
  RowDataErrors,
  RenderInputTypeParams,
} from "../../../../types";
import RenderCoRelationRows from "./RenderCoRelationRows";
import {
  RecordSectionTitle,
  RecordPageSectionLayout,
  RecordSectionHeader,
} from "../../../../utils/styled";
import { validateSingleArrayObjectItem } from "../../validations";
import { customEvaluations } from "../../evaluations";
import { useUIStateContext } from "../../../../contexts/UIStateContext";
import { useTranslation } from "react-i18next";
import i18n from "../../../../translate/i18next";
import { SectionInfoIconWithTooltip } from "../../../common/Tooltip";
import { resolveCustomStyle } from "../../style-config";
import { DynamicAdvancedFieldRenderer } from "./DynamicAdvancedFieldRenderer";
import { ARRAY_SECTION_RENDERERS } from "../custom-renderers/arraySectionRenderers";

export default function RenderCoRelation({
  propertyKey,
  property,
  inputValue: inputValueObject,
  evalMethod,
  dependencyRules,
  triggerDependencyRule,
  setupBaseRequestInfo,
  selectedTabSectionKey,
  rowDataReducerState,
  dispatch,
}: Readonly<RenderCoRelationProps>): JSX.Element | null {
  const { t } = useTranslation();
  const { minimum: minRows, maximum: maxRows } = property;
  const { isReadOnly, schema } = rowDataReducerState;
  const { selected } = useUserContext();
  const { setRecordPageToast, isRecordOpen } = useUIStateContext();

  const [rows, setRows] = useState<number[]>([]);
  const nextId = useRef(1);
  const isAddButtonDisabled =
    (!!maxRows && rows.length >= maxRows) || !!property.disabled;
  const isDeleteButtonDisabled =
    (!!minRows && rows.length <= minRows) || !!property.disabled;
  const isReadOnlyRecord = !!isReadOnly;

  const { items: propertyItems } = property;
  const {
    properties,
    required = [],
    rowScopedProperties,
  } = (propertyItems as JsonSchemaItems) || {};

  useEffect(() => {
    const initialRows = inputValueObject?.map((_, index) => index) || [];
    setRows(initialRows);
  }, [inputValueObject]);

  useEffect(() => {
    if (evalMethod && inputValueObject) {
      customEvaluations({
        evalMethod,
        propertyKey,
        inputValue: inputValueObject,
        selectedTabSectionKey,
        rowDataReducerState,
        dispatch,
      });
    }
  }, [inputValueObject]);

  const addRow = () => {
    const newRowIndex = nextId.current++;
    setRows([...rows, newRowIndex]);

    dispatch({
      type: "ADD_OBJECT_ROW",
      payload: {
        propertyKey,
        rowIndex: newRowIndex,
        selectedTabSectionKey,
      },
    });
  };

  const deleteRow = (rowIndex: number) => {
    setRows(rows.filter((_, index) => index !== rowIndex));
    const { currentRowObjectId, deletedObjectWithInactiveStatus } =
      getRowObjectToBeDeleted(
        propertyKey,
        rowIndex,
        rowDataReducerState.rowData,
        selectedTabSectionKey,
      );

    if (currentRowObjectId) {
      dispatch({
        type: "SET_DELETED_OBJECTS",
        payload: {
          propertyKey,
          newFieldValue: deletedObjectWithInactiveStatus,
          selectedTabSectionKey,
        },
      });
    }

    dispatch({
      type: "DELETE_OBJECT_ROW",
      payload: {
        propertyKey,
        rowIndex,
        selectedTabSectionKey,
      },
    });
  };

  const updateRow = (rowIndex: number, newFieldValue: RowDataObject) => {
    dispatch({
      type: "UPDATE_OBJECT_ROW",
      payload: {
        propertyKey,
        rowIndex,
        newFieldValue,
        selectedTabSectionKey,
      },
    });
  };

  const canAddSingleRow = (rowIndex: number): boolean => {
    const { itemHasErrors, itemErrors } = validateSingleArrayObjectItem(
      rowIndex,
      inputValueObject[rowIndex],
      propertyKey,
      property,
      rowDataReducerState,
      selectedTabSectionKey,
    );

    if (itemHasErrors) {
      dispatch({
        type: "UPDATE_ROW_DATA_REDUCER_STATE",
        payload: {
          rowDataErrors: {
            ...rowDataReducerState.rowDataErrors,
            [propertyKey]: {
              ...rowDataReducerState.rowDataErrors[propertyKey],
              [rowIndex]: itemErrors,
            },
          } as RowDataErrors,
        },
      });
      return false;
    }

    return true;
  };

  if (!properties) return null;

  const renderInputType = ({
    rowIndex,
    propertySubKey,
    itemProperty,
    isDialogField = false,
    isInline = false,
  }: RenderInputTypeParams) => (
    <DynamicAdvancedFieldRenderer
      propertyKey={propertyKey}
      properties={properties}
      required={required}
      rowScopedProperties={rowScopedProperties}
      inputValueObject={inputValueObject}
      dependencyRules={dependencyRules}
      triggerDependencyRule={triggerDependencyRule}
      rowIndex={rowIndex}
      propertySubKey={propertySubKey}
      itemProperty={itemProperty}
      isDialogField={isDialogField}
      isInline={isInline}
    />
  );

  const rowsToDisplay = () => {
    const SectionRenderer = ARRAY_SECTION_RENDERERS[propertyKey];

    if (SectionRenderer) {
      return (
        <SectionRenderer
          propertyKey={propertyKey}
          property={property}
          properties={properties}
          inputValueObject={inputValueObject}
          rows={rows}
          isAddButtonDisabled={isAddButtonDisabled}
          isDeleteButtonDisabled={isDeleteButtonDisabled}
          isReadOnly={isReadOnlyRecord}
          rowDataReducerState={rowDataReducerState}
          setupBaseRequestInfo={setupBaseRequestInfo}
          selectedTabSectionKey={selectedTabSectionKey}
          dependencyRules={dependencyRules}
          addRow={addRow}
          deleteRow={deleteRow}
          updateRow={updateRow}
          renderInputType={renderInputType}
          canAddSingleRow={canAddSingleRow}
          setRecordPageToast={setRecordPageToast}
          dispatch={dispatch}
        />
      );
    }

    return (
      <RenderCoRelationRows
        inputValueObject={inputValueObject}
        rows={rows}
        propertyKey={propertyKey}
        property={property}
        isAddButtonDisabled={isAddButtonDisabled}
        isDeleteButtonDisabled={isDeleteButtonDisabled}
        isReadOnly={isReadOnlyRecord}
        addRow={addRow}
        deleteRow={deleteRow}
        renderInputType={renderInputType}
        dispatch={dispatch}
      />
    );
  };

  return (
    <RecordPageSectionLayout>
      <RecordSectionHeader>
        <RecordSectionTitle
          $customStyle={resolveCustomStyle({
            applicability: "section-title",
            selectedSetup: selected.setup.value,
            sectionKey: propertyKey,
            isRecordOpen: isRecordOpen,
          })}
        >
          {getTitleFromKey(propertyKey, t, i18n.language)}
        </RecordSectionTitle>
        <SectionInfoIconWithTooltip
          customStyle={resolveCustomStyle({
            applicability: "section-info-button",
            selectedSetup: selected.setup.value,
            sectionKey: propertyKey,
            isRecordOpen: isRecordOpen,
          })}
          tooltip={getSectionTooltipInfo({
            selectedTabSectionKey,
            sectionKey: propertyKey,
            schema,
          })}
        />
      </RecordSectionHeader>
      {rowsToDisplay()}
    </RecordPageSectionLayout>
  );
}
